import React from 'react';
import MattergenForm from './mattergen/MattergenForm';
import FM4MForm from './fm4m/FM4MForm';
import GT4SDForm from './gt4sd/GT4SDForm';
import { useModelPolicy } from '../hooks/useCapability';

function ManualFormPanel({
  manualModels,
  setManualModels,
  manualElements,
  setManualElements,
  manualMGElementsOut,
  setManualMGElementsOut,
  manualMGDimensionality,
  setManualMGDimensionality,
  manualMGStructureHints,
  setManualMGStructureHints,
  manualMGPretrainedOverride,
  setManualMGPretrainedOverride,
  manualProperty,
  setManualProperty,
  manualPropertyValue,
  setManualPropertyValue,
  manualMGGuidance,
  setManualMGGuidance,
  manualMGRecordTraj,
  setManualMGRecordTraj,
  manualMGChunkSize,
  setManualMGChunkSize,
  manualNumCandidates,
  setManualNumCandidates,
  manualMolecularCategory,
  setManualMolecularCategory,
  manualSmiles,
  setManualSmiles,
  manualFMModel,
  setManualFMModel,
  manualFMReturnMode,
  setManualFMReturnMode,
  manualFMMaxItemsInline,
  setManualFMMaxItemsInline,
  manualFMChunkSize,
  setManualFMChunkSize,
  manualGT4SDAlgorithm,
  setManualGT4SDAlgorithm,
  manualGT4SDAlgorithmVersion,
  setManualGT4SDAlgorithmVersion,
  manualGT4SDTargetSmiles,
  setManualGT4SDTargetSmiles,
  manualGT4SDTargetProperty,
  setManualGT4SDTargetProperty,
  manualGT4SDTargetValue,
  setManualGT4SDTargetValue,
  manualGT4SDNumSamples,
  setManualGT4SDNumSamples,
  mgErrors,
  fmErrors,
  gtErrors,
  loading,
  hasManualErrors,
  onSubmit,
}) {
  // Settled decision 5 in manual mode (Part 1a, D16): a model the org disabled or weighted to
  // zero gets no checkbox and no form. AppProvider masks its tick the same way.
  const policy = useModelPolicy();
  const runnable = (svc) => !!policy[svc]?.enabled && policy[svc].weight > 0;
  return (
    <form onSubmit={onSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {runnable('mattergen') && (
          <label>
            <input
              type="checkbox"
              checked={manualModels.mattergen}
              onChange={(e) => setManualModels((m) => ({ ...m, mattergen: e.target.checked }))}
            />{' '}
            MatterGen
          </label>
        )}
        {runnable('fm4m') && (
          <label>
            <input
              type="checkbox"
              checked={manualModels.fm4m}
              onChange={(e) => setManualModels((m) => ({ ...m, fm4m: e.target.checked }))}
            />{' '}
            FM4M
          </label>
        )}
        {runnable('gt4sd') && (
          <label>
            <input
              type="checkbox"
              checked={manualModels.gt4sd}
              onChange={(e) => setManualModels((m) => ({ ...m, gt4sd: e.target.checked }))}
            />{' '}
            GT4SD
          </label>
        )}
      </div>

      {runnable('mattergen') && manualModels.mattergen && (
        <MattergenForm
          elements={manualElements}
          setElements={setManualElements}
          elementsOut={manualMGElementsOut}
          setElementsOut={setManualMGElementsOut}
          dimensionality={manualMGDimensionality}
          setDimensionality={setManualMGDimensionality}
          structureHints={manualMGStructureHints}
          setStructureHints={setManualMGStructureHints}
          pretrainedOverride={manualMGPretrainedOverride}
          setPretrainedOverride={setManualMGPretrainedOverride}
          property={manualProperty}
          setProperty={setManualProperty}
          propertyValue={manualPropertyValue}
          setPropertyValue={setManualPropertyValue}
          guidance={manualMGGuidance}
          setGuidance={setManualMGGuidance}
          recordTraj={manualMGRecordTraj}
          setRecordTraj={setManualMGRecordTraj}
          chunkSize={manualMGChunkSize}
          setChunkSize={setManualMGChunkSize}
          numCandidates={manualNumCandidates}
          setNumCandidates={setManualNumCandidates}
          errors={mgErrors}
        />
      )}

      {runnable('fm4m') && manualModels.fm4m && (
        <FM4MForm
          category={manualMolecularCategory}
          setCategory={setManualMolecularCategory}
          smiles={manualSmiles}
          setSmiles={setManualSmiles}
          model={manualFMModel}
          setModel={setManualFMModel}
          returnMode={manualFMReturnMode}
          setReturnMode={setManualFMReturnMode}
          maxItemsInline={manualFMMaxItemsInline}
          setMaxItemsInline={setManualFMMaxItemsInline}
          chunkSize={manualFMChunkSize}
          setChunkSize={setManualFMChunkSize}
          errors={fmErrors}
        />
      )}

      {runnable('gt4sd') && manualModels.gt4sd && (
        <GT4SDForm
          algorithm={manualGT4SDAlgorithm}
          setAlgorithm={setManualGT4SDAlgorithm}
          algorithmVersion={manualGT4SDAlgorithmVersion}
          setAlgorithmVersion={setManualGT4SDAlgorithmVersion}
          targetSmiles={manualGT4SDTargetSmiles}
          setTargetSmiles={setManualGT4SDTargetSmiles}
          targetProperty={manualGT4SDTargetProperty}
          setTargetProperty={setManualGT4SDTargetProperty}
          targetValue={manualGT4SDTargetValue}
          setTargetValue={setManualGT4SDTargetValue}
          numSamples={manualGT4SDNumSamples}
          setNumSamples={setManualGT4SDNumSamples}
          errors={gtErrors}
        />
      )}

      <button type="submit" disabled={loading || hasManualErrors}>
        {loading ? 'Working...' : 'Run'}
      </button>
    </form>
  );
}

export default ManualFormPanel;
